import { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWAState {
  isInstallable: boolean;
  isInstalled: boolean;
  isUpdateAvailable: boolean;
  showInstallPrompt: boolean;
}

export function usePWA() {
  const [pwaState, setPWAState] = useState<PWAState>({
    isInstallable: false,
    isInstalled: false,
    isUpdateAvailable: false,
    showInstallPrompt: false
  });
  
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    let onVisibility: (() => void) | null = null;
    // 1. Detectar se já está instalado
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                        (window.navigator as any).standalone === true;
    
    if (isStandalone) {
      setPWAState(prev => ({ ...prev, isInstalled: true }));
    }

    // Verificar se já foi dispensado recentemente (últimos 7 dias)
    const dismissedAt = localStorage.getItem('pwa-install-dismissed');
    const shouldShowPrompt = !dismissedAt || 
      (Date.now() - parseInt(dismissedAt)) > 7 * 24 * 60 * 60 * 1000;

    // 2. Capturar evento beforeinstallprompt (Android/Desktop)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(promptEvent);
      setPWAState(prev => ({ 
        ...prev, 
        isInstallable: true,
        showInstallPrompt: !isStandalone && shouldShowPrompt
      }));
      console.log('✅ [PWA] Install prompt available');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // 3. Detectar quando o app foi instalado
    const handleAppInstalled = () => {
      console.log('✅ [PWA] App installed successfully');
      setPWAState(prev => ({ 
        ...prev, 
        isInstalled: true, 
        isInstallable: false,
        showInstallPrompt: false 
      }));
      setDeferredPrompt(null);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    // 4. Registar Service Worker e verificar atualizações
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js', { scope: '/' })
        .then(reg => {
          console.log('✅ [PWA] Service Worker registered:', reg);
          setRegistration(reg);

           // Check for updates immediately on load (helps bust stale caches in iOS PWA)
           reg.update();

          // Verificar atualizações a cada 30 min
          setInterval(() => {
            reg.update();
          }, 30 * 60 * 1000);

           // Also check when returning to foreground
           onVisibility = () => {
             if (document.visibilityState === 'visible') {
               reg.update();
             }
           };
           document.addEventListener('visibilitychange', onVisibility);

          // Detectar atualização disponível
          reg.addEventListener('updatefound', () => {
            const newWorker = reg.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  // Only show update banner if worker is actually waiting
                  if (reg.waiting) {
                    console.log('🔄 [PWA] Update available (worker waiting)');
                    setPWAState(prev => ({ ...prev, isUpdateAvailable: true }));
                  } else {
                    console.log('ℹ️ [PWA] Worker installed but already activating (auto mode)');
                  }
                }
              });
            }
          });
        })
        .catch(error => {
          console.warn('⚠️ [PWA] Service Worker registration failed:', error);
        });
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      if (onVisibility) {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
  }, []);

  // Função para mostrar prompt de instalação
  const promptInstall = async (): Promise<boolean> => {
    if (!deferredPrompt) {
      console.warn('⚠️ [PWA] No install prompt available');
      return false;
    }

    try {
      await deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        console.log('✅ [PWA] User accepted the install prompt');
        setPWAState(prev => ({ ...prev, showInstallPrompt: false }));
        return true;
      } else {
        console.log('❌ [PWA] User dismissed the install prompt');
        return false;
      }
    } catch (error) {
      console.error('❌ [PWA] Error showing install prompt:', error);
      return false;
    } finally {
      setDeferredPrompt(null);
    }
  };

  // Função para aplicar atualização
  const applyUpdate = () => {
    if (!registration) {
      console.warn('⚠️ [PWA] No registration');
      return;
    }

    if (registration.waiting) {
      // Modo prompt: worker está à espera, enviar skipWaiting
      console.log('🔄 [PWA] Sending SKIP_WAITING to waiting worker...');
      const onControllerChange = () => {
        console.log('✅ [PWA] New Service Worker is now controlling the page');
        window.location.reload();
      };
      navigator.serviceWorker.addEventListener('controllerchange', onControllerChange, { once: true });
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    } else {
      // Modo auto: worker já se ativou sozinho, basta recarregar
      console.log('🔄 [PWA] No waiting worker (auto mode) — reloading...');
      window.location.reload();
    }
  };

  // Fechar prompt de instalação
  const dismissInstallPrompt = () => {
    setPWAState(prev => ({ ...prev, showInstallPrompt: false }));
    
    // Guardar no localStorage para não mostrar novamente por 7 dias
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  return {
    ...pwaState,
    promptInstall,
    applyUpdate,
    dismissInstallPrompt,
    registration
  };
}
